import { EmailSettingPlaceholders } from '@Components/Mailing';
import { EditSetting } from '@Components/Settings';
import { MAILING_API } from '@Constants/backendConstants';
import { useSettings } from '@Hooks/Settings';
import { Enum, EnumMailingSettingGroup, EnumMailingSettingModule } from '@Model';
import {
    Card,
    EditFrameMode,
    EditFrameShell,
    Grid,
    GridColumn,
    isOk,
    LoadingFrame,
    useToasts,
} from '@synqo/frontend-component-library';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useClientNavigate } from '../../../../Routing/useClientNavigate';
import { ApiEmailSetting } from './FrameListEmailSettings';

export interface FrameEditSettingProps {
    name: string;
}

export function FrameEditEmailSettingsGroup<TSettingNameEnum extends Enum>(props: FrameEditSettingProps) {
    const { itemId } = useParams();
    const navigate = useClientNavigate();
    const [mode, setMode] = useState(EditFrameMode.View);
    const { pushAllToasts } = useToasts();

    const { settings, updateSettingLocally, updateSettings, reset } = useSettings<
        TSettingNameEnum,
        EnumMailingSettingGroup,
        EnumMailingSettingModule
    >(`${MAILING_API}/settings/${props.name}`);

    const settingsInGroup = settings.filter((s) => s.setting_group.toString() == itemId);
    const anySettingInGroup: ApiEmailSetting<TSettingNameEnum> | undefined =
        settingsInGroup.length === 0 ? undefined : settingsInGroup[0];

    const label = anySettingInGroup?.label.split(' - ', 1)[0] ?? '';

    const headerProps = useMemo(
        () => ({
            label: label,
            resourceLabel: label,
            onBack: () => navigate(-1),
        }),
        [navigate, label],
    );

    const bannerProps = useMemo(
        () => ({
            mode: mode,
            loading: false,
            enterEditMode: () => {
                setMode(EditFrameMode.Edit);
                reset();
                return true;
            },
            onCancel: () => {
                setMode(EditFrameMode.View);
                reset();
                return true;
            },
            onSave: async () => {
                setMode(EditFrameMode.View);
                const result = await updateSettings();
                if (!isOk(result)) {
                    pushAllToasts(result.messages);
                    return false;
                }
                return true;
            },
        }),
        [mode, reset, updateSettings, pushAllToasts],
    );

    if (settings.length === 0) {
        return <LoadingFrame />;
    }

    if (anySettingInGroup === undefined) {
        return 'Nicht gefunden';
    }

    return (
        <EditFrameShell key={mode} headerProps={headerProps} bannerProps={bannerProps}>
            <Card name="frame" border="withoutBorder">
                <Grid columns={12}>
                    <GridColumn span={8}>
                        <Card name="settings" collapsable={false} border="withoutBorder">
                            {settingsInGroup.map((setting) => (
                                <EditSetting
                                    key={setting.id}
                                    setting={setting}
                                    onChange={updateSettingLocally}
                                    locked={mode === EditFrameMode.View}
                                />
                            ))}
                        </Card>
                    </GridColumn>
                    <GridColumn span={4}>
                        <EmailSettingPlaceholders settingGroup={anySettingInGroup.setting_group} />
                    </GridColumn>
                </Grid>
            </Card>
        </EditFrameShell>
    );
}
